import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, MessageSquareText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useEquipesParaFiltro } from '@/hooks/useTarefas';
import { buscarEmpresasPainelSms, buscarPainelSms } from '@/lib/leadSmsApi';
import { formatarCNPJ } from '@/utils/formatters';

const primeiro = (n?: string | null) => {
  const p = String(n || '').split(' ')[0];
  return p ? p.charAt(0) + p.slice(1).toLowerCase() : '—';
};

const pct = (parte: number, todo: number) => (todo > 0 ? `${Math.round((parte / todo) * 100)}%` : '—');

const dataHora = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

export function usePainelSms(dias: number, equipeId: string | null, habilitado = true) {
  return useQuery({
    queryKey: ['sms', 'painel', dias, equipeId],
    enabled: habilitado,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: () => buscarPainelSms(dias, equipeId),
  });
}

/**
 * PAINEL DE SMS DO TOPO (Douglas 02/10/2026) — o mesmo desenho do painel de
 * Tarefas (PainelTarefasTopo): "quero saber quantos clicaram no link e abriram
 * realmente o chamado no WhatsApp do consultor vindo dos SMS".
 * Chamou = a mensagem pronta do link chegou no Inbox; abrir o link não conta.
 * Aproveitamento = empresas que chamaram ÷ empresas com SMS entregue.
 * O clique num nome abre as empresas dele, com o botão Abrir conversa.
 */
export function PainelSmsTopo({ aberto, onFechar, podeFiltrarEquipe }: {
  aberto: boolean;
  onFechar: () => void;
  podeFiltrarEquipe: boolean;
}) {
  const navigate = useNavigate();
  const [dias, setDias] = useState(30);
  const [equipeId, setEquipeId] = useState<string | null>(null);
  const [pessoa, setPessoa] = useState<{ id: string; nome: string; papel: 'dono' | 'enviou' } | null>(null);
  const painel = usePainelSms(dias, equipeId, aberto);
  const equipes = useEquipesParaFiltro(aberto && podeFiltrarEquipe);
  const empresas = useQuery({
    queryKey: ['sms', 'painel', 'empresas', dias, equipeId, pessoa?.id, pessoa?.papel],
    enabled: aberto && !!pessoa,
    staleTime: 60_000,
    queryFn: () => buscarEmpresasPainelSms(dias, equipeId, pessoa!.id, pessoa!.papel),
  });
  const d = painel.data;

  const abrirConversa = (id: string) => {
    onFechar();
    navigate(`/whatsapp-inbox?conversation=${encodeURIComponent(id)}`);
  };

  const filtro = 'h-[26px] rounded-lg border border-white/[0.18] bg-white/[0.12] px-2 text-[11px] text-white focus:outline-none [&>option]:text-foreground';
  const th = 'px-1 py-1.5 text-left text-[10px] font-bold uppercase tracking-[0.6px] text-muted-foreground';
  const td = 'px-1 py-1.5 border-t border-border/60';
  const tile = 'rounded-lg bg-[#FAF8F4] px-2.5 py-2 dark:bg-[#241A1A]';

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v) onFechar(); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[680px] max-h-[88vh] flex flex-col overflow-hidden p-0 gap-0 rounded-2xl sm:rounded-2xl bg-background [&>button]:hidden">
        <div className="flex shrink-0 items-center gap-3 bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-4 py-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-white/[0.12] text-white"><MessageSquareText className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-[15px] font-bold tracking-[-0.3px] text-white">SMS · quem envia e quem chamou</DialogTitle>
            <p className="truncate text-[10.5px] text-white/65">Últimos {dias} dias · {equipeId ? (equipes.data?.find((e) => e.id === equipeId)?.nome ?? 'equipe') : 'todas as equipes que você vê'}</p>
          </div>
          <select value={dias} onChange={(e) => setDias(Number(e.target.value))} className={filtro} aria-label="Período">
            <option value={7}>7 dias</option><option value={30}>30 dias</option><option value={90}>90 dias</option>
          </select>
          {podeFiltrarEquipe && (
            <select value={equipeId ?? ''} onChange={(e) => setEquipeId(e.target.value || null)} className={filtro} aria-label="Equipe">
              <option value="">Equipe</option>
              {(equipes.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          )}
          <button type="button" aria-label="Fechar" onClick={onFechar}
            className="grid h-[26px] w-[26px] place-items-center rounded-lg border border-white/[0.14] bg-white/[0.12] text-white hover:bg-white/[0.22]">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {painel.isLoading && <p className="text-[11px] text-muted-foreground">carregando…</p>}
          {painel.error && <p role="alert" className="text-[11px] text-destructive">{(painel.error as Error).message}</p>}
          {d && !pessoa && <>
            <div className="grid grid-cols-4 gap-2">
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">SMS enviados</p><p className="font-display text-[20px] font-bold tabular-nums">{d.sms}</p><p className="text-[10px] text-muted-foreground">{d.disparos} disparos</p></div>
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">Chegaram</p><p className="font-display text-[20px] font-bold tabular-nums text-success">{d.chegaram}</p><p className="text-[10px] text-muted-foreground">{d.empresas_entregues} empresas</p></div>
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">Não chegaram</p><p className="font-display text-[20px] font-bold tabular-nums text-primary">{d.nao_chegaram}</p><p className="text-[10px] text-muted-foreground">número morto</p></div>
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">Chamaram pelo SMS</p><p className="font-display text-[20px] font-bold tabular-nums text-success">{d.chamaram} <span className="text-[13px]">· {pct(d.chamaram, d.empresas_entregues)}</span></p><p className="text-[10px] text-muted-foreground">de {d.empresas_entregues} entregues</p></div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <p className="mb-1 text-[12px] font-semibold">Quem mais envia <span className="font-normal text-muted-foreground">· qualquer cargo</span></p>
                <table className="w-full table-fixed text-[12px]">
                  <thead><tr><th className={cn(th, 'w-[42%]')}>Enviou</th><th className={cn(th, 'text-right')}>SMS</th><th className={cn(th, 'text-right')}>Chegou</th><th className={cn(th, 'text-right')}>Não cheg.</th></tr></thead>
                  <tbody>
                    {d.quem_envia.map((l, i) => (
                      <tr key={`${l.usuario_id}-${i}`} onClick={() => l.usuario_id && setPessoa({ id: l.usuario_id, nome: primeiro(l.nome), papel: 'enviou' })} className="cursor-pointer hover:bg-muted/50">
                        <td className={cn(td, 'truncate')}>{primeiro(l.nome)} <span className="text-[10px] text-muted-foreground">{l.cargo}</span></td>
                        <td className={cn(td, 'text-right tabular-nums')}>{l.sms}</td>
                        <td className={cn(td, 'text-right tabular-nums', l.chegou > 0 && 'text-success')}>{l.chegou}</td>
                        <td className={cn(td, 'text-right tabular-nums', l.nao_chegou > 0 && 'font-semibold text-primary')}>{l.nao_chegou}</td>
                      </tr>
                    ))}
                    {!d.quem_envia.length && <tr><td colSpan={4} className={cn(td, 'text-muted-foreground')}>Nenhum SMS no período.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div>
                <p className="mb-1 text-[12px] font-semibold">Aproveitamento <span className="font-normal text-muted-foreground">· só consultor dono</span></p>
                <table className="w-full table-fixed text-[12px]">
                  <thead><tr><th className={cn(th, 'w-[32%]')}>Consultor</th><th className={cn(th, 'text-right')}>Disparos</th><th className={cn(th, 'text-right')}>Entreg.</th><th className={cn(th, 'text-right')}>Chamou</th><th className={cn(th, 'text-right')}>Aprov.</th></tr></thead>
                  <tbody>
                    {d.aproveitamento.map((l) => (
                      <tr key={l.usuario_id} onClick={() => setPessoa({ id: l.usuario_id, nome: primeiro(l.nome), papel: 'dono' })} className="cursor-pointer hover:bg-muted/50">
                        <td className={cn(td, 'truncate')}>{primeiro(l.nome)}</td>
                        <td className={cn(td, 'text-right tabular-nums')}>{l.disparos}</td>
                        <td className={cn(td, 'text-right tabular-nums')}>{l.entregues}</td>
                        <td className={cn(td, 'text-right tabular-nums', l.chamou > 0 && 'font-semibold text-success underline underline-offset-2')}>{l.chamou}</td>
                        <td className={cn(td, 'text-right tabular-nums font-semibold')}>{pct(l.chamou, l.entregues)}</td>
                      </tr>
                    ))}
                    {!d.aproveitamento.length && <tr><td colSpan={5} className={cn(td, 'text-muted-foreground')}>Nenhum disparo de consultor no período.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground">
              Disparo = uma empresa que recebeu a Apresentação SMS. Chamou = o cliente clicou no link e mandou a mensagem pronta no WhatsApp; abrir o link sem mandar não conta.
              Aproveitamento = empresas que chamaram ÷ empresas com pelo menos 1 SMS entregue. Clique num nome: abre as empresas dele, com o botão Abrir conversa.
            </p>
          </>}

          {pessoa && <>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPessoa(null)}
                className="inline-flex h-[26px] items-center gap-1 rounded-lg border border-border px-2 text-[11px] hover:bg-muted/50">
                <ArrowLeft className="h-3 w-3" /> Voltar
              </button>
              <p className="text-[12px] font-semibold">{pessoa.nome} <span className="font-normal text-muted-foreground">· {pessoa.papel === 'dono' ? 'empresas dele que receberam SMS' : 'SMS que ele enviou'}</span></p>
            </div>
            {empresas.isLoading && <p className="text-[11px] text-muted-foreground">carregando…</p>}
            {empresas.error && <p role="alert" className="text-[11px] text-destructive">{(empresas.error as Error).message}</p>}
            {empresas.data && (
              <table className="w-full table-fixed text-[12px]">
                <thead><tr><th className={cn(th, 'w-[34%]')}>Empresa</th><th className={cn(th, 'w-[14%]')}>SMS saiu</th><th className={cn(th, 'w-[10%] text-right')}>Chegou</th><th className={cn(th, 'w-[14%]')}>Chamou</th><th className={th}>Mensagem</th></tr></thead>
                <tbody>
                  {empresas.data.map((e) => (
                    <tr key={e.cnpj}>
                      <td className={cn(td, 'truncate')} title={e.razao_social ?? ''}>
                        {e.razao_social || '—'}
                        <span className="block text-[10px] text-muted-foreground tabular-nums">{formatarCNPJ(e.cnpj)}</span>
                      </td>
                      <td className={cn(td, 'tabular-nums')}>{dataHora(e.enviado_em)}</td>
                      <td className={cn(td, 'text-right tabular-nums')}>{e.chegou}/{e.sms}</td>
                      <td className={cn(td, 'tabular-nums', e.chamou_em && 'font-semibold text-success')}>{e.chamou_em ? dataHora(e.chamou_em) : '—'}</td>
                      <td className={td}>
                        {e.conversa_id ? (
                          <div className="flex items-center gap-2">
                            <span className="min-w-0 flex-1 truncate text-muted-foreground" title={e.mensagem ?? ''}>{e.mensagem || '—'}</span>
                            <button type="button" onClick={() => abrirConversa(e.conversa_id!)}
                              className="h-[24px] shrink-0 rounded-lg border border-border px-2 text-[11px] font-semibold text-primary hover:bg-muted/50">
                              Abrir conversa
                            </button>
                          </div>
                        ) : <span className="text-muted-foreground">ainda não chamou</span>}
                      </td>
                    </tr>
                  ))}
                  {!empresas.data.length && <tr><td colSpan={5} className={cn(td, 'text-muted-foreground')}>Nenhuma empresa no período.</td></tr>}
                </tbody>
              </table>
            )}
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
